import { IAssignmentTask, tClientLicence } from 'common/modules/ReactShared';
import {
  IDocument,
  IDocumentsListItem,
  tAssignableType
} from 'common/modules/ReactShared/data/api/document/types';
import { TASK_TO_MESSENGER_TYPE_MAP } from './constants';
import { tAssignmentNormalizedData, tAssignmentsNormalizedListItem } from './types';

const DEFAULT_MINIMUM_ANSWER_LENGTH = 5;

type tAssignmentDirectDocumentAdapterArgs = {
  assignmentDoc: IDocument;
};
function assignmentDocumentAdapter({
  assignmentDoc
}: tAssignmentDirectDocumentAdapterArgs): tAssignmentNormalizedData {
  const { assignable, avgResponseTime } = assignmentDoc;
  const { minimumDuration, minimumWords } = assignable;
  return {
    assignmentTask: {
      author: {
        brand: assignable.author?.avatarURL,
        name: assignable.author?.name || ''
      },
      title: assignable.name,
      text: assignable.instructions,
      audioURL: assignable.audioURL,
      videoURL: assignable.videoURL,
      imageURL: assignable.imageURL,
      imageAlt: assignable.imageAlt || '',
      mobileImageURL: assignable.mobileImageURL,
      giphyImage: assignable.giphyImage,
      coverImg: assignable.coverImg,
      createdAt: null,
      lesson: assignable.lesson,
      kind: assignable.kind,
      avgResponseTime,
      ...getAnswerInfo(assignable.type, { minimumDuration, minimumWords })
    },
    status: assignmentDoc.status,
    conversation: assignmentDoc.conversation || [],
    estimatedReviewTime: assignmentDoc.team.responseTime,
    rating: assignmentDoc.rating,
    assignmentId: assignmentDoc.id
  };
}

type tAssignmentTaskAdapterArgs = {
  assignmentTask: IAssignmentTask;
  license: tClientLicence;
};
function assignmentTaskAdapter({
  assignmentTask,
  license
}: tAssignmentTaskAdapterArgs): tAssignmentNormalizedData {
  return {
    assignmentTask: {
      author: { brand: license?.imageURL, name: license?.name || '' },
      title: assignmentTask.title,
      text: assignmentTask.introduction,
      audioURL: assignmentTask.audioURL,
      videoURL: assignmentTask.videoURL,
      imageURL: assignmentTask.imageURL,
      imageAlt: assignmentTask.imageAlt,
      mobileImageURL: assignmentTask.mobileImageURL,
      giphyImage: assignmentTask.giphyImage,
      coverImg: assignmentTask.coverImg,
      createdAt: null,
      ...getAnswerInfo(assignmentTask.type, assignmentTask.items[0])
    },
    conversation: [],
    status: 'pending',
    estimatedReviewTime: null,
    rating: null,
    assignmentId: null
  };
}

type tAssignmentsListItemAdapterArgs = {
  item: IDocumentsListItem;
};
type tContext = {
  imageURL: string | null;
  name: string;
  type: 'lesson' | 'school';
} | null;

function assignmentsListItemAdapter({
  item
}: tAssignmentsListItemAdapterArgs): tAssignmentsNormalizedListItem {
  const { id, status, createdAt, updatedAt, assignable, team } = item;
  let context: tContext = null;
  if (assignable.lesson) {
    context = {
      imageURL: assignable.lesson.imageURL,
      name: assignable.lesson.title,
      type: 'lesson' as const
    };
  } else if (team) {
    context = {
      imageURL: team.brand,
      name: team.name,
      type: 'school' as const
    };
  }
  return {
    title: assignable.name,
    createdAt,
    updatedAt,
    context,
    status,
    assignmentId: id
  };
}

export { assignmentDocumentAdapter, assignmentTaskAdapter, assignmentsListItemAdapter };

function getAnswerInfo(
  type: tAssignableType,
  item: { minimumDuration?: number | null; minimumWords?: number | null }
) {
  const answerType = TASK_TO_MESSENGER_TYPE_MAP[type];
  const minimalAnswerLength =
    (answerType === 'audio' ? item.minimumDuration : item.minimumWords) ??
    DEFAULT_MINIMUM_ANSWER_LENGTH;

  return {
    answerType,
    minimalAnswerLength
  };
}
